import { NgFor, NgIf } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { NotificationService } from '../../../../Services/notification.service';
import { Router, RouterLink } from '@angular/router';
import { inject } from '@angular/core';
import { FormsModule } from '@angular/forms'; // Import FormsModule
import autoTable from 'jspdf-autotable'; // For PDF export
import * as XLSX from 'xlsx'; // For Excel export
import jsPDF from 'jspdf'; // For PDF export
import FileSaver from 'file-saver'; // For file saving
import { StockInService } from '../../../../Services/Stock_Transactions/Stock_in/stock-in.service';
import { environment } from '../../../../../environments/environment';

@Component({
  selector: 'app-stock-in-list',
  standalone: true,
  imports: [NgFor, FormsModule, NgIf],
  templateUrl: './stock-in-list.component.html',
  styleUrl: './stock-in-list.component.css',
})
export class StockInListComponent implements OnInit {
  paginatedStockInList: any[] = [];
  currentPage = 1;
  itemsPerPage = 10;
  totalPages = 0;
  stockInList: any[] = []; // Initialize as an empty array
  startDate: string = '';
  endDate: string = '';
  filteredStockInList: any[] = [];

  // New properties for filtering, sorting, and column selection
  searchValues: { [key: string]: string } = {};
  sortKey: string = '';
  sortDirection: 'asc' | 'desc' = 'asc';
  showDropdown: boolean = false;
  globalSearchText: string = '';

  // Define the default set of fields for stock in products
  defaultFields = [
    { key: 'st_image1', label: 'Product Image', isImage: true, selected: true },
    { key: 'stin_item_code', label: 'Item Code', selected: true },
    { key: 'stin_metal_type', label: 'Metal Type', selected: true },
    { key: 'stin_product_type', label: 'Product Type', selected: true },
    { key: 'stin_item_category', label: 'Item Category', selected: true },
    { key: 'stin_item_name', label: 'Item Name', selected: true },
    { key: 'stin_item_size', label: 'Item Size', selected: true },
    { key: 'stin_color', label: 'Color', selected: true },
    { key: 'stin_purity', label: 'Purity', selected: true },
    { key: 'stin_quantity', label: 'Quantity', selected: true },
    { key: 'stin_metal_rate', label: 'Metal Rate', selected: true },
    { key: 'stin_unit_price', label: 'Total Price', selected: true },
    { key: 'stin_unit_quantity', label: 'Unit Quantity', selected: true },
    { key: 'stin_sub_unit_price', label: 'Per Peice Price', selected: true },
    { key: 'stin_total_wt', label: 'Total Weight', selected: true },
    { key: 'stin_net_weight', label: 'Fine Weight', selected: true },
    { key: 'stin_making_charges', label: 'Making Charges', selected: true },
    { key: 'stin_lab_charges', label: 'Lab Charges', selected: true },
    { key: 'stin_per_gram_labour', label: 'Per Gram Labour', selected: true },
    { key: 'stin_per_piece_labour', label: 'Per Piece Labour', selected: true },
    { key: 'stin_per_gm_shipping', label: 'Per GM Shipping', selected: true },
    { key: 'stin_per_piece_shipping', label: 'Per Piece Shipping', selected: true },
    { key: 'stin_total_per_piece_price', label: 'Total Per Piece Price', selected: true },
    { key: 'stin_per_piece_silver_price', label: 'Per Piece Silver Price', selected: true },
    { key: 'stin_silver_rate', label: 'Silver Rate', selected: true },
    { key: 'stin_per_piece_weight', label: 'Per Piece Weight', selected: true },
    { key: 'stin_subunit', label: 'Subunit', selected: true },
    { key: 'stin_per_gram_cost_without_packaging', label: 'W/O Packaging(Cost/GM)', selected: true },
    { key: 'stin_per_gram_cost_with_packaging', label: 'With Packaging(Cost/GM)', selected: true },
    { key: 'stin_entry_date', label: 'Entry Date', selected: true },
    // { key: 'stin_supplier_name', label: 'Supplier Name', selected: true },
  ];

  selectedFields: any[] = [];
  titleTag: boolean = true;

  constructor(
    private stockInService: StockInService, // Use the new service
    private notificationService: NotificationService
  ) { }

  router = inject(Router);

  ngOnInit(): void {
    this.loadSelectedFields();
    this.getStockInList();
    this.hideTitle();
  }

  hideTitle() {
    const currentUrl = this.router.url;
    if (currentUrl.includes('report')) {
      this.titleTag = false;
    }
  }

  loadSelectedFields() {
    const stockInSelection = localStorage.getItem('stockInSelected');
    if (stockInSelection) {

      const storedFields = JSON.parse(stockInSelection);

      // Create a map for quick lookup of stored field selections
      const storedFieldMap = new Map(
        storedFields.map((field: any) => [field.key, field.selected])
      );

      // Initialize selectedFields based on defaultFields, applying stored selections
      this.selectedFields = this.defaultFields.map((defaultField) => ({
        ...defaultField,
        selected: storedFieldMap.has(defaultField.key)
          ? storedFieldMap.get(defaultField.key)
          : defaultField.selected, // Use default if not found in storage
      }));
    } else {
      // If no stored preferences, use the default fields
      this.selectedFields = [...this.defaultFields];
    }
  }

  saveSelectedFields() {
    localStorage.setItem('stockInSelected', JSON.stringify(this.selectedFields));
  }

  getStockInList() {
    this.stockInService.getStockInEntries(new HttpParams()).subscribe(
      (response: any) => {
        if (response?.data?.length) {
          this.stockInList = response.data.reverse();
          this.filterTable(); // Apply filters and pagination after fetching data
        } else {
          this.stockInList = [];
          this.paginatedStockInList = [];
          this.totalPages = 0;
        }
      },
      (error: any) => {
        this.notificationService.showError(
          'Failed to fetch stock in entries.',
          'Error'
        );
        this.stockInList = [];
        this.paginatedStockInList = [];
        this.totalPages = 0;
      }
    );
  }

  editStockIn(id: any) {
    // Implement navigation or modal for editing if needed
  }

  filterTable() {

    if (!this.stockInList) return;

    let filtered = this.stockInList.filter((item: any) => {

      /* ================= DATE FILTER ================= */

      if (this.startDate || this.endDate) {

        const itemDateRaw = item['stin_entry_date'];
        if (!itemDateRaw) return false;

        const itemDate = new Date(itemDateRaw).setHours(0, 0, 0, 0);

        const start = this.startDate
          ? new Date(this.startDate).setHours(0, 0, 0, 0)
          : null;

        const end = this.endDate
          ? new Date(this.endDate).setHours(0, 0, 0, 0)
          : null;

        if (start && itemDate < start) return false;
        if (end && itemDate > end) return false;
      }

      /* ================= COLUMN FILTER ================= */

      const matchesFieldFilters = this.selectedFields.every((field) => {

        if (!field.selected) return true;

        const searchTerm = (this.searchValues[field.key] || '')
          .trim()
          .toLowerCase();

        if (!searchTerm) return true;

        const fieldValue = (item[field.key] ?? '')
          .toString()
          .toLowerCase();

        return fieldValue.includes(searchTerm);
      });

      /* ================= GLOBAL SEARCH ================= */

      const matchesGlobalSearch = this.globalSearchText
        ? Object.values(item).some((val) =>
          (val ?? '')
            .toString()
            .toLowerCase()
            .includes(this.globalSearchText.trim().toLowerCase())
        )
        : true;

      return matchesFieldFilters && matchesGlobalSearch;
    });

    /* ================= SORT ================= */

    if (this.sortKey) {
      filtered.sort((a: any, b: any) => {

        const valA = a[this.sortKey] ?? '';
        const valB = b[this.sortKey] ?? '';

        return this.sortDirection === 'asc'
          ? valA > valB ? 1 : valA < valB ? -1 : 0
          : valA < valB ? 1 : valA > valB ? -1 : 0;
      });
    }

    /* ================= SAVE FILTERED ================= */

    this.filteredStockInList = filtered;

    /* ================= PAGINATION ================= */

    this.totalPages = Math.ceil(filtered.length / this.itemsPerPage);
    this.currentPage = Math.min(this.currentPage, this.totalPages || 1);

    const start = (this.currentPage - 1) * this.itemsPerPage;

    this.paginatedStockInList = filtered.slice(
      start,
      start + this.itemsPerPage
    );
  }


  sortBy(key: string) {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDirection = 'asc';
    }
    this.filterTable();
  }

  onPageChange(page: number) {
    this.currentPage = page;
    this.filterTable();
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.filterTable(); // Re-apply filter to update pagination
    }
  }

  goToPreviousPage() {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.filterTable();
    }
  }

  goToNextPage() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.filterTable();
    }
  }

  get pageNumbers(): number[] {
    const pageNumbers: number[] = [];
    const maxPagesToShow = 5;

    if (this.totalPages <= maxPagesToShow) {
      for (let i = 1; i <= this.totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);

      // Show ellipsis if current page is far from the start
      if (this.currentPage > 2) {
        pageNumbers.push(-1); // Sentinel for '...'
      }

      // Determine range of pages around current page
      let start = Math.max(
        2,
        this.currentPage - Math.floor(maxPagesToShow / 2) + 1
      );
      let end = Math.min(
        this.totalPages - 1,
        this.currentPage + Math.floor(maxPagesToShow / 2) - 1
      );

      // Adjust range if current page is near start or end
      if (this.currentPage <= Math.floor(maxPagesToShow / 2) + 1) {
        end = Math.min(this.totalPages - 1, maxPagesToShow - 1);
      }
      if (this.currentPage >= this.totalPages - Math.floor(maxPagesToShow / 2)) {
        start = Math.max(2, this.totalPages - maxPagesToShow + 2);
      }

      for (let i = start; i <= end; i++) {
        pageNumbers.push(i);
      }

      // Show ellipsis if current page is far from the end
      if (this.currentPage < this.totalPages - 1) {
        if (
          pageNumbers[pageNumbers.length - 1] !== this.totalPages &&
          pageNumbers[pageNumbers.length - 1] !== -1
        ) {
          pageNumbers.push(-1); // Sentinel for '...'
        }
      }

      // Always show last page if not already in the visible range
      if (!pageNumbers.includes(this.totalPages) && this.totalPages > 1) {
        pageNumbers.push(this.totalPages);
      }
    }

    // Filter out duplicate -1 (ellipsis) and ensure page numbers are unique and sorted
    const uniquePageNumbers = Array.from(new Set(pageNumbers)).sort(
      (a, b) => a - b
    );
    return uniquePageNumbers;
  }

  // New getter for 'select all' checkbox
  get allFieldsSelected(): boolean {
    return this.selectedFields.every(field => field.selected);
  }

  // New getter for 'deselect all' checkbox
  get noFieldsSelected(): boolean {
    return this.selectedFields.every(field => !field.selected);
  }

  selectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = true));
    this.saveSelectedFields();
    this.filterTable(); // Re-apply filter to show/hide columns
  }

  deselectAllFields() {
    this.selectedFields.forEach((field) => (field.selected = false));
    this.saveSelectedFields();
    this.filterTable(); // Re-apply filter to show/hide columns
  }

  get selectedFieldss() { // Renamed from selectedFields because of "get selectedFields" error
    return this.selectedFields.filter((field) => field.selected);
  }

  applyColumnSelection() {
    this.saveSelectedFields();
    this.showDropdown = false;
    this.filterTable(); // Re-apply filter to update the table display
  }

  /* ============================================================
     IMAGE PROXY HELPERS
  ============================================================ */

  proxyImageUrl(url: string): string {

    if (!url) return '';

    const backendBase = environment.apiBaseUrl.replace(/\/api\/$/, '');

    const match = url.match(/\/storage\/(.+)$/);

    if (match) {
      return `${backendBase}/api/image-proxy/${match[1]}`;
    }

    return `${backendBase}/api/image-proxy/${url}`;
  }


  onImageError(event: any) {
    event.target.src = 'https://via.placeholder.com/50';
  }

  /* ============================================================
     LOAD IMAGE AS BASE64
  ============================================================ */

  async loadImageBase64(url: string): Promise<string | null> {
    try {
      const proxiedUrl = this.proxyImageUrl(url);
      console.log('Loading image:', proxiedUrl);
      const res = await fetch(proxiedUrl);
      if (!res.ok) {
        console.error(' HTTP error:', res.status);
        throw new Error(res.statusText);
      }
      const blob = await res.blob();
      return await new Promise(resolve => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error('Image load FAILED:', url, err);
      return null;
    }
  }

  /* ============================================================
     PRELOAD ALL PDF IMAGES
  ============================================================ */

  async preparePdfImages(): Promise<void> {
    console.log(' Preparing images for PDF...');
    const dataToProcess = this.getExportSourceData();
    for (const row of dataToProcess as any[]) {
      if (!row.st_image1) continue;
      console.log('➡ Row:', row.id, row.st_image1);
      row.__img = await this.loadImageBase64(row.st_image1);
      console.log(
        row.__img ? 'Image OK' : ' Image NULL'
      );
    }
    console.log(' Image preload finished');
  }

  /* ============================================================
     MAIN PDF EXPORT
  ============================================================ */

  async downloadPDF(): Promise<void> {
    //  Preload images first
    await this.preparePdfImages();

    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a3',
    });

    const selectedFields = this.selectedFields.filter(f => f.selected);

    if (!selectedFields.length) {
      this.notificationService.showError(
        'Please select at least one column',
        'Error'
      );
      return;
    }

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    /* ================= HEADER ================= */

    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Stock In Report', pageWidth / 2, 15, { align: 'center' });

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text(
      `Generated on: ${new Date().toLocaleString()}`,
      pageWidth - 20,
      10,
      { align: 'right' }
    );

    /* ================= TABLE ================= */

    const headers = [
      { header: 'No.', dataKey: 'no' },
      ...selectedFields.map(f => ({
        header: f.label,
        dataKey: f.key,
      })),
    ];

    const sourceData = this.getExportSourceData();

    const body = sourceData.map((item, rowIndex) => {

      const row: any = { no: rowIndex + 1 };

      selectedFields.forEach(f => {
        if (f.key === 'st_image1') {
          row[f.key] = '';          //  NEVER render base64 text
          row.__img = item.__img;  //  store image here
        } else {
          row[f.key] = this.getDisplayValue(item[f.key]);
        }
      });

      return row;
    });

    autoTable(doc, {
      columns: headers,
      body,

      startY: 22,

      margin: { left: 6, right: 6 },

      theme: 'grid',

      // 🔥 critical — stop sideways pagination
      horizontalPageBreak: false,

      rowPageBreak: 'auto',

      // 🔥 force table to page width
      tableWidth: pageWidth - 12,

      styles: {
        fontSize: 6.2,      // smaller text
        cellPadding: 1.3,  // tighter
        overflow: 'linebreak',
        valign: 'middle',
      },

      headStyles: {
        fillColor: [193, 151, 37],
        textColor: 255,
        halign: 'center',
        fontSize: 7,
        fontStyle: 'bold',
      },

      bodyStyles: {
        textColor: 40,
      },

      alternateRowStyles: {
        fillColor: [245, 245, 245],
      },

      /* ---------- IMAGE DRAWING ---------- */
      didParseCell: (data: any) => {
        // Only body rows
        if (data.section !== 'body') return;

        // Only image column
        if (data.column.dataKey === 'st_image1') {
          data.cell.styles.minCellHeight = 32;
          data.cell.styles.halign = 'center';
          data.cell.styles.valign = 'middle';
        }
      },

      didDrawCell: (data: any) => {
        if (data.column.dataKey !== 'st_image1') return;
        const rowData: any = data.row.raw;
        const img = rowData?.__img;
        if (!img) return;

        try {
          const padding = 3;
          const imgW = 28;
          const imgH = 28;
          const x = data.cell.x + (data.cell.width - imgW) / 2;
          const y = data.cell.y + (data.cell.height - imgH) / 2;

          doc.addImage(
            img,
            'JPEG',
            x,
            y,
            imgW,
            imgH,
            undefined,
            'FAST'
          );
        } catch (err) {
          console.error(' addImage failed:', err);
        }
      },

      didDrawPage: () => {
        doc.setFontSize(8);
        doc.text(
          `Page ${(doc as any).getNumberOfPages()}`,
          pageWidth / 2,
          pageHeight - 10,
          { align: 'center' }
        );
      },

      columnStyles: {
        no: { cellWidth: 12, halign: 'center' },
        st_image1: { cellWidth: 38 },
      },
    });

    doc.save('stock-in-report.pdf');
  }



  downloadExcel(): void {
    const selectedFields = this.selectedFields.filter((field) => field.selected);

    // ✅ all numeric backend keys
    const numericKeys = [
      'stin_purity',
      'stin_quantity',
      'stin_metal_rate',
      'stin_unit_price',
      'stin_unit_quantity',
      'stin_sub_unit_price',
      'stin_total_wt',
      'stin_fine_weight',
      'stin_making_charges',
      'stin_lab_charges',
      'stin_per_gram_labour',
      'stin_per_piece_labour',
      'stin_per_gm_shipping',
      'stin_per_piece_shipping',
      'stin_total_per_piece_price',
      'stin_per_piece_silver_price',
      'stin_silver_rate',
      'stin_per_piece_weight',
      'stin_per_gram_cost_without_packaging',
      'stin_per_gram_cost_with_packaging'
    ];

    const sourceData = this.getExportSourceData();

    const data = sourceData.map((item, index) => {

      const row: any = { 'No.': index + 1 };

      selectedFields.forEach((field) => {
        let value: any;

        // normal field (no formatting)
        value = item[field.key];

        // ✅ force numeric fields
        if (numericKeys.includes(field.key)) {
          value =
            value !== null &&
              value !== undefined &&
              value !== ''
              ? Number(value)
              : 0;
        }

        row[field.label] = value;
      });

      return row;
    });

    const worksheet: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);

    // ✅ HARD force number cell types
    Object.keys(worksheet).forEach((cell) => {
      if (cell.startsWith('!')) return;

      const c = worksheet[cell];
      if (typeof c.v === 'number') {
        c.t = 'n';
      }
    });

    const workbook: XLSX.WorkBook = {
      Sheets: { 'Stock In List': worksheet },
      SheetNames: ['Stock In List'],
    };

    const excelBuffer: any = XLSX.write(workbook, {
      bookType: 'xlsx',
      type: 'array',
    });

    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type:
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
      'stock-in-list.xlsx'
    );
  }


  downloadJSONAll(): void {
    const data = this.getExportSourceData();

    const jsonString = JSON.stringify(data, null, 2);

    const blob = new Blob([jsonString], { type: 'application/json' });
    const fileName = 'stock-in-list.json';

    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    link.click();

    URL.revokeObjectURL(link.href);
  }

  downloadCSV(): void {
    const data = this.getExportSourceData();

    if (!data.length) return;

    const selectedFields = this.selectedFields?.filter((f) => f.selected) || [];
    const headers = ['No.', ...selectedFields.map((f) => f.label)];
    const keys = selectedFields.map((f) => f.key);

    let csvContent = headers.join(',') + '\n';

    data.forEach((item, index) => {
      const row = [
        index + 1,
        ...keys.map((key) => {
          const value = this.getDisplayValue(item[key]);
          return typeof value === 'string' && value.includes(',')
            ? `"${value}"`
            : value ?? '';
        }),
      ];
      csvContent += row.join(',') + '\n';
    });

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const fileName = 'stock-in-list.csv';

    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', fileName);
    link.click();

    URL.revokeObjectURL(link.href);
  }

  toggleDropdown() {
    this.showDropdown = !this.showDropdown;
  }

  restoreColumnSelection() {
    localStorage.removeItem('stockInSelected');
    this.selectedFields = [...this.defaultFields]; // Restore from the default set
    this.filterTable(); // Re-apply filter
  }

  async printTable() {
    await this.preparePdfImages();


    let printContent = `
  <html>
  <head>
    <title>Stock In List</title>

    <style>

      body {
        font-family: Arial, sans-serif;
      }

      /* ===== TABLE GRID ===== */

      table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
        font-size: 12px;
      }

      th,
      td {
        border-right: 2px solid #000;
        border-bottom: 2px solid #000;
        padding: 8px;
        text-align: left;
        vertical-align: middle;
        white-space: nowrap;
      }

      th:first-child,
      td:first-child {
        border-left: 2px solid #000;
      }

      thead th {
        border-top: 2px solid #000;
        background-color: #f2f2f2;
        font-weight: 600;
      }

      tbody tr:last-child td {
        border-bottom: 2px solid #000;
      }

      /* ===== PRINT ===== */

      @media print {

        @page {
         size: auto;
          margin: 8mm;
        }

        body {
          margin: 0;
        }

        thead {
          display: table-header-group;
        }

        tr {
          page-break-inside: avoid;
        }
      }

    </style>

  </head>

  <body>

    <h3>Stock In List</h3>

    <table>
      <thead>
        <tr>
          <th>No.</th>
  `;

    // 🧾 HEADERS
    this.selectedFieldss.forEach(field => {
      printContent += `<th>${field.label}</th>`;
    });

    printContent += `
        </tr>
      </thead>
      <tbody>
  `;

    // 📦 ROWS
    const data = this.getExportSourceData();

    data.forEach((item, index) => {

      printContent += `
      <tr>
        <td>${(this.currentPage - 1) * this.itemsPerPage + index + 1}</td>
    `;

      this.selectedFieldss.forEach(field => {
        if (field.key === 'st_image1') {
          const img = item.__img ? `<img src="${item.__img}" style="width:50px;height:50px;object-fit:contain;" />` : '';
          printContent += `<td>${img}</td>`;
        } else {
          printContent += `<td>${this.getDisplayValue(item[field.key])}</td>`;
        }
      });

      printContent += `
      </tr>
    `;
    });

    printContent += `
      </tbody>
    </table>

  </body>
  </html>
  `;

    // 🖨 OPEN PRINT WINDOW
    const printWindow = window.open('', '_blank');

    if (printWindow) {
      printWindow.document.open();
      printWindow.document.write(printContent);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 200);
      };
    }
  }


  onFieldToggle(field: any) {
    // The [(ngModel)] handles the field.selected change directly.
    // We just need to save the state and re-filter.
    this.saveSelectedFields();
    this.filterTable();
  }

  // getDisplayValue(value: any): string {
  //   if (value == null) return '';
  //   // Handle boolean values specifically for better display
  //   if (typeof value === 'boolean') {
  //     return value ? 'Yes' : 'No';
  //   }
  //   return String(value);
  // }
  getDisplayValue(value: any): string {
    if (value == null) return '';
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }
  private getExportSourceData(): any[] {

    const hasFilter =
      this.startDate ||
      this.endDate ||
      this.globalSearchText ||
      Object.values(this.searchValues).some(v => v);

    return hasFilter
      ? this.filteredStockInList
      : this.stockInList;
  }

}
